import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash, PencilSimple, FloppyDisk } from '@phosphor-icons/react';
import { API } from './adminConstants';

export function QueryCategoryManagement() {
  const { getAuthHeader } = useAuth();
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ label: '', value: '', assigned_users: [], order: 0 });

  const load = async () => {
    try {
      const [catRes, userRes] = await Promise.all([
        axios.get(`${API}/admin/query-categories`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeader() })
      ]);
      setCategories(catRes.data);
      setUsers(userRes.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ label: '', value: '', assigned_users: [], order: categories.length }); setEditCat(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.label) { toast.error('Label required'); return; }
    const payload = { ...form, value: form.value || form.label.toLowerCase().replace(/[^a-z0-9]+/g, '-') };
    try {
      if (editCat) { await axios.put(`${API}/admin/query-categories/${editCat.id}`, payload, { headers: getAuthHeader() }); toast.success('Updated'); }
      else { await axios.post(`${API}/admin/query-categories`, payload, { headers: getAuthHeader() }); toast.success('Created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (cat) => { setEditCat(cat); setForm({ label: cat.label || '', value: cat.value || '', assigned_users: cat.assigned_users || [], order: cat.order || 0 }); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/admin/query-categories/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };

  const toggleUser = (userId) => {
    setForm(prev => ({ ...prev, assigned_users: prev.assigned_users.includes(userId) ? prev.assigned_users.filter(u => u !== userId) : [...prev.assigned_users, userId] }));
  };
  const getUserName = (userId) => { const u = users.find(u => u.id === userId); return u ? `${u.first_name} ${u.last_name}` : userId; };

  return (
    <div data-testid="query-category-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Query Categories</h1><p className="text-slate-500 text-sm">Manage categories for the general query function and assign users who receive queries</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-query-cat-btn"><Plus size={15} className="mr-1" />Add Category</Button>
      </div>
      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="query-cat-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editCat ? 'Edit Category' : 'New Category'}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><Label className="text-xs">Display Label *</Label><Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. HR / Payroll" data-testid="query-cat-label" /></div>
              <div><Label className="text-xs">Slug (auto)</Label><Input value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="hr-payroll" /></div>
              <div><Label className="text-xs">Order</Label><Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} min="0" /></div>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Assigned Users (receive queries from this category)</Label>
              <div className="border border-slate-200 rounded-lg max-h-52 overflow-y-auto">
                {users.filter(u => u.is_active !== false).map(u => (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0" data-testid={`query-cat-user-${u.id}`}>
                    <input type="checkbox" checked={form.assigned_users.includes(u.id)} onChange={() => toggleUser(u.id)} className="rounded border-slate-300 text-lime-600 focus:ring-lime-500" />
                    <div className="flex-1 min-w-0"><span className="text-sm text-slate-900">{u.first_name} {u.last_name}</span><span className="text-xs text-slate-400 ml-2">{u.role}</span></div>
                  </label>
                ))}
              </div>
              {form.assigned_users.length > 0 && <p className="text-[11px] text-lime-600 mt-1">{form.assigned_users.length} user(s) assigned</p>}
            </div>
          </div>
          <div className="flex gap-3 mt-5"><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-query-cat-btn"><FloppyDisk size={15} className="mr-1" />{editCat ? 'Update' : 'Create'}</Button></div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Order</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Label</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Slug</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Assigned Users</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => (
              <tr key={cat.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`query-cat-row-${cat.id}`}>
                <td className="px-4 py-3 text-sm text-slate-500">{cat.order}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{cat.label}</td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono text-xs">{cat.value}</td>
                <td className="px-4 py-3">
                  {(cat.assigned_users || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">{cat.assigned_users.map(uid => (
                      <span key={uid} className="text-[10px] px-2 py-0.5 bg-lime-100 text-lime-700 rounded">{getUserName(uid)}</span>
                    ))}</div>
                  ) : <span className="text-xs text-slate-400">None</span>}
                </td>
                <td className="px-4 py-3"><div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(cat)} className="h-7 w-7 p-0"><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(cat.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700"><Trash size={14} /></Button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {categories.length === 0 && <div className="p-8 text-center text-slate-500">No categories configured</div>}
      </div>
    </div>
  );
}
